/**
 * Service zur Zentralisierung des Sentry-Benutzerkontext-Managements aus JWT-Tokens.
 *
 * Dieser Service eliminiert Code-Duplizierung in der Anwendung wo JWT-Tokens
 * dekodiert und zum Setzen des Sentry-Benutzerkontexts verwendet werden. Alle JWT→Sentry
 * Benutzerkontext-Logik ist jetzt hier zentralisiert für bessere Security Review und Wartbarkeit.
 *
 * @see Prinzip #10 (Security-First Development)
 * @see Prinzip #3 (Konfiguration zentral validieren)
 */

import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/angular-ivy';

/**
 * Keycloak JWT Payload-Struktur mit Benutzerinformationen.
 *
 * Dieses Interface definiert die erwartete Form von dekodierten Keycloak JWT-Tokens.
 * Verwendet für typsichere Extraktion von Benutzerkontext-Daten.
 */
export interface KeycloakJwtPayload {
  /** Subject (Benutzer-ID) aus Keycloak */
  sub: string;
  /** E-Mail-Adresse des Benutzers */
  email: string;
  /** Bevorzugter Benutzername des Benutzers */
  preferred_username: string;
  /** Vollständiger Name des Benutzers */
  name: string;
  /** Token-Ablaufzeitstempel */
  exp?: number;
  /** Token-Ausstellungszeitstempel */
  iat?: number;
  /** Aussteller */
  iss?: string;
}

/**
 * Service zum Setzen des Sentry-Benutzerkontexts aus JWT-Tokens.
 *
 * Zentralisiert die Logik des Dekodierens von JWT-Tokens und Befüllens des Sentry-Benutzerkontexts.
 * Dies gewährleistet konsistentes Benutzer-Tracking über Fehlerberichte hinweg und reduziert Code-Duplizierung.
 *
 * @example
 * ```typescript
 * // Grundlegende Verwendung (Standalone-Modus - von Keycloak Access Token)
 * constructor(private sentryUserContext: SentryUserContextService) {}
 *
 * this.oidcSecurityService.getAccessToken().subscribe(token => {
 *   this.sentryUserContext.setUserContextFromToken(token);
 * });
 * ```
 *
 * @example
 * ```typescript
 * // Mit zusätzlichem Fehlerkontext (Embedded-Modus - von Portal Token)
 * const portalToken = this.portalTokenService.getAdnovaToken();
 * this.sentryUserContext.setUserContextFromToken(portalToken, {
 *   runningEmbedded: true,
 *   portalVersion: '2.1.0'
 * });
 * ```
 *
 * @example
 * ```typescript
 * // In NgRx Effect mit Error Extras
 * this.sentryUserContext.setUserContextFromToken(token, {
 *   action: 'ChatActions.sendMessage',
 *   conversationId: 'conv-123',
 *   errorCode: error.statusCode
 * });
 * Sentry.captureException(error); // Benutzerkontext automatisch enthalten
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class SentryUserContextService {
  /**
   * Setzt Sentry-Benutzerkontext aus einem JWT-Access-Token.
   *
   * Dekodiert das bereitgestellte JWT-Token, extrahiert Benutzerinformationen (id, email, username, name),
   * und setzt diese als Sentry-Benutzerkontext für Error-Tracking. Setzt optional zusätzliche
   * Kontextdaten via extras Parameter.
   *
   * **Security-Hinweis**: Diese Methode behandelt sensible Benutzerdaten aus JWT-Tokens.
   * Stellen Sie sicher dass Tokens aus vertrauenswürdigen Quellen stammen (Keycloak, PortalTokenService).
   *
   * @param token - JWT-Access-Token (von Keycloak oder Portal)
   * @param extras - Optionaler zusätzlicher Kontext zum Anhängen an Sentry Scope (z.B. Fehler-Metadaten)
   *
   * @throws {Error} Wenn Token ungültig ist oder nicht dekodiert werden kann
   *
   * @example
   * ```typescript
   * // Standalone-Modus (Keycloak Token)
   * this.setUserContextFromToken(keycloakAccessToken);
   *
   * // Embedded-Modus (Portal Token) mit Kontext
   * this.setUserContextFromToken(portalToken, { runningEmbedded: true });
   *
   * // Fehler-Reporting mit zusätzlichen Metadaten
   * this.setUserContextFromToken(token, {
   *   conversationId: 'conv-123',
   *   messageCount: 5,
   *   errorType: 'StreamingTimeout'
   * });
   * ```
   */
  setUserContextFromToken(token: string, extras?: Record<string, unknown>): void {
    // JWT-Token dekodieren um Benutzerinformationen zu extrahieren
    const decodedToken = jwtDecode<KeycloakJwtPayload>(token);

    // Benutzerfelder aus Token-Payload extrahieren
    const email = decodedToken.email;
    const username = decodedToken.preferred_username;
    const name = decodedToken.name;

    // Sentry-Benutzerkontext innerhalb isoliertem Scope setzen
    Sentry.withScope((scope) => {
      // Existierendes User-Objekt holen oder neues erstellen
      let user = scope.getUser();
      if (!user) {
        user = {};
      }

      // Benutzerkontext aus JWT-Payload befüllen
      user.id = decodedToken.sub;
      user.email = email;
      user.username = username;
      user['name'] = name; // Bracket-Notation verwenden da 'name' nicht im Standard User Interface ist

      // Sentry Scope mit Benutzerkontext aktualisieren
      scope.setUser(user);

      // Zusätzlichen Kontext anhängen falls bereitgestellt (z.B. Fehler-Metadaten, Environment-Info)
      if (extras) {
        scope.setExtras(extras);
      }
    });
  }
}
